import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { formatDate, formatDateTime, formatEuro } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Avatar, Banner, Button, Eyebrow, Pill, Skeleton, Tabs, useToast } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useContactRecord } from '../contacts/api';
import { useNextStepGate } from '../contacts/editors/shared';
import { bookAppointmentHref, isNextStepRequired } from '../tasks/api';
import { markReviewed, useInvalidateSelfDisclosure, useSelfDisclosureFull, type FullView } from './api';
import { CompareTab } from './CompareTab';
import { ViewTab } from './ViewTab';
import { VolumeSidebar, VolumeTab } from './VolumeTab';
import styles from './SelfDisclosurePage.module.css';

type TabKey = 'view' | 'compare' | 'volume';

const TABS: ReadonlyArray<{ value: TabKey; label: string }> = [
  { value: 'view', label: 'Ansicht' },
  { value: 'compare', label: 'Versionsvergleich' },
  { value: 'volume', label: 'Volumen' },
];

function tabOf(value: string | null): TabKey {
  return value === 'compare' || value === 'volume' ? value : 'view';
}

/** „vollständig" (Selbstauskunft 08/09) for a signed version, the API's label otherwise. */
export function statusPill(full: FullView): {
  label: string;
  variant: 'success' | 'warning' | 'info' | 'neutral';
} {
  const status = full.selfDisclosure.status;
  if (status === 'submitted' || status === 'reviewed') return { label: 'vollständig', variant: 'success' };
  if (status === 'in_progress') return { label: full.selfDisclosure.statusLabel, variant: 'warning' };
  return { label: full.selfDisclosure.statusLabel, variant: 'info' };
}

/**
 * `/contacts/:id/self-disclosure/:selfDisclosureId` — Selbstauskunft 08 (Ansicht), 09 (Versionsvergleich),
 * 10 (Volumen) as one page: contact header, the three tabs, the dark volume card and „Sichtbar für".
 */
export function SelfDisclosurePage() {
  const { id: contactId = '', selfDisclosureId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const invalidate = useInvalidateSelfDisclosure();
  const full = useSelfDisclosureFull(selfDisclosureId);
  const record = useContactRecord(contactId);
  const tab = tabOf(params.get('tab'));
  const [reviewing, setReviewing] = useState(false);

  const gate = useNextStepGate(full.data?.contact.name ?? '', async (step) => {
    const done = await markReviewed(selfDisclosureId, step);
    await invalidate();
    toast({ message: 'Selbstauskunft als geprüft markiert.' });
    if (done.next === 'book_appointment') await navigate(bookAppointmentHref(contactId, done.createdTaskId));
  });

  function setTab(next: TabKey) {
    const search = new URLSearchParams(params);
    if (next === 'view') search.delete('tab');
    else search.set('tab', next);
    setParams(search, { replace: true });
  }

  async function review() {
    setReviewing(true);
    try {
      const done = await markReviewed(selfDisclosureId);
      await invalidate();
      toast({ message: 'Selbstauskunft als geprüft markiert.' });
      if (done.next === 'book_appointment')
        await navigate(bookAppointmentHref(contactId, done.createdTaskId));
    } catch (cause) {
      if (isNextStepRequired(cause)) gate.open();
      else toast({ message: errorMessage(cause) });
    } finally {
      setReviewing(false);
    }
  }

  const data = full.data;
  const header = record.data?.header;
  const dealItem = record.data?.linked.find((group) => group.key === 'deals')?.items[0];
  const headerPill = dealItem?.status ? `Deal · ${dealItem.status.label}` : (header?.lifecycle.label ?? null);
  const contactName = data?.contact.name ?? header?.name ?? '';
  const initials =
    header?.initials ??
    contactName
      .split(/\s+/)
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase();
  const contactLine = header
    ? [header.phone, header.email, header.city, header.owner ? `Betreuer ${header.owner.name}` : null]
        .filter(Boolean)
        .join(' · ')
    : '';

  if (full.isError) {
    const error = full.error;
    const forbidden = error instanceof ApiError && error.status === 403;
    return (
      <div className={styles.page}>
        <Banner
          tone={forbidden ? 'info' : 'danger'}
          actions={
            <Link to={`/contacts/${contactId}`} className={styles.bannerLink}>
              Zur Kontaktakte
            </Link>
          }
        >
          {errorMessage(error)}
        </Banner>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <Avatar initials={initials || '?'} name={contactName} size={40} className={styles.avatar} />
        <div className={styles.headText}>
          <div className={styles.crumb}>
            <Link to="/contacts" className={styles.crumbLink}>
              Kontakte
            </Link>
            <span>/</span>
            <Link to={`/contacts/${contactId}`} className={styles.crumbLink}>
              {contactName || '…'}
            </Link>
            <span>/</span>
            <span>Selbstauskunft{data ? ` · Version ${data.selfDisclosure.version}` : ''}</span>
          </div>
          <div className={styles.titleRow}>
            {contactName ? (
              <h1 className={styles.title}>{contactName}</h1>
            ) : (
              <Skeleton width={220} height={24} />
            )}
            {headerPill ? <Pill variant="info">{headerPill}</Pill> : null}
          </div>
          {contactLine ? <div className={styles.contactLine}>{contactLine}</div> : null}
        </div>
        <div className={styles.headActions}>
          <Link to={`/contacts/${contactId}?editor=call`} className={styles.headButton}>
            Anrufen
          </Link>
          <Link to={`/contacts/${contactId}?editor=email`} className={styles.headButton}>
            E-Mail
          </Link>
          <Link to={`/contacts/${contactId}?editor=whatsapp`} className={styles.headButton}>
            WhatsApp
          </Link>
        </div>
      </div>
      <Tabs items={TABS} value={tab} onChange={setTab} label="Selbstauskunft" className={styles.tabs} />

      {full.isPending || !data ? (
        <div className={styles.grid} aria-busy="true">
          <div className={styles.card}>
            <Skeleton width="30%" height={20} />
            <div className={styles.skeletonRows}>
              <Skeleton width="60%" />
              <Skeleton width="45%" />
              <Skeleton width="70%" />
              <Skeleton width="50%" />
            </div>
          </div>
          <div className={styles.column}>
            <Skeleton variant="block" height={150} />
          </div>
        </div>
      ) : (
        <div className={styles.grid}>
          <div className={clsx(styles.column, styles.mainColumn)}>
            {data.selfDisclosure.kindLabel === 'Korrektur Kunde' &&
            data.canMarkReviewed &&
            tab !== 'volume' ? (
              <div className={styles.infoBanner} role="status">
                <div className={styles.infoBannerText}>
                  <span className={styles.infoBannerTitle}>
                    {data.contact.name} hat seine Angaben korrigiert
                  </span>
                  <span className={styles.infoBannerMeta}>
                    Version {data.selfDisclosure.version}
                    {data.selfDisclosure.submittedAt
                      ? ` vom ${formatDateTime(data.selfDisclosure.submittedAt).replace(' · ', ', ')}`
                      : ''}
                    {` · ${data.selfDisclosure.changedCount} ${data.selfDisclosure.changedCount === 1 ? 'Änderung' : 'Änderungen'} · neu unterschrieben`}
                  </span>
                </div>
                <Button variant="secondary" size="small" loading={reviewing} onClick={() => void review()}>
                  Als gesehen markieren
                </Button>
              </div>
            ) : null}
            {tab === 'view' ? (
              <ViewTab full={data} reviewing={reviewing} onReview={() => void review()} />
            ) : tab === 'compare' ? (
              <CompareTab full={data} />
            ) : (
              <VolumeTab full={data} />
            )}
          </div>
          <aside className={styles.column} aria-label="Zusammenfassung">
            {tab === 'volume' ? (
              <VolumeSidebar full={data} />
            ) : (
              <div className={styles.darkCard}>
                <Eyebrow as="div" className={styles.darkEyebrow}>
                  {data.volume?.manual ? 'Verwendetes Volumen' : 'Finanzierbares Volumen'}
                </Eyebrow>
                {data.volume ? (
                  <>
                    <div className={styles.darkValue}>{formatEuro(data.volume.usedVolumeCents)}</div>
                    <div className={styles.darkSub}>{data.volume.subline}</div>
                    {data.volume.manual ? (
                      <div className={styles.darkBadge}>
                        <Pill variant="accent">manuell angepasst</Pill>
                        <span className={styles.darkSub}>
                          rechnerisch {formatEuro(data.volume.computedVolumeCents)}
                        </span>
                      </div>
                    ) : null}
                    {data.volume.expiresAt ? (
                      <div className={styles.darkSub}>gültig bis {formatDate(data.volume.expiresAt)}</div>
                    ) : null}
                    <button type="button" className={styles.darkLink} onClick={() => setTab('volume')}>
                      Berechnung ansehen
                    </button>
                  </>
                ) : (
                  <div className={styles.darkSub}>Entsteht mit der Einreichung der Selbstauskunft.</div>
                )}
              </div>
            )}
            {data.selfDisclosure.submittedBy ? (
              <div className={styles.card}>
                <Eyebrow as="div">Tippgeber</Eyebrow>
                <div className={styles.rows}>
                  {data.selfDisclosure.referrerPartner ? (
                    <div className={styles.row}>
                      <span className={styles.rowLabel}>Tippgeber-Partner</span>
                      <span className={styles.rowValue}>{data.selfDisclosure.referrerPartner.name}</span>
                    </div>
                  ) : null}
                  <div className={styles.row}>
                    <span className={styles.rowLabel}>Eingereicht von</span>
                    <span className={styles.rowValue}>
                      {data.selfDisclosure.submittedBy.name}, {data.selfDisclosure.submittedBy.email}
                    </span>
                  </div>
                  {data.selfDisclosure.submittedBy.phone ? (
                    <div className={styles.row}>
                      <span className={styles.rowLabel}>Telefon</span>
                      <span className={styles.rowValue}>{data.selfDisclosure.submittedBy.phone}</span>
                    </div>
                  ) : null}
                </div>
                {data.selfDisclosure.submittedBy.note ? (
                  <p className={styles.cardNote}>{data.selfDisclosure.submittedBy.note}</p>
                ) : null}
              </div>
            ) : null}
            {data.versions.length > 1 ? (
              <div className={styles.card}>
                <Eyebrow as="div">Versionen</Eyebrow>
                <div className={styles.rows}>
                  {data.versions.map((version) => {
                    const current = version.id === data.selfDisclosure.id;
                    const label = `V${version.version}${version.submittedAt ? ` · ${formatDate(version.submittedAt).slice(0, 6)}` : ` · ${version.statusLabel}`}`;
                    return (
                      <div key={version.id} className={styles.row}>
                        <span className={styles.rowLabel}>
                          {current ? (
                            <span className={styles.rowCurrent}>{label}</span>
                          ) : (
                            <Link
                              to={`/contacts/${contactId}/self-disclosure/${version.id}${tab === 'view' ? '' : `?tab=${tab}`}`}
                              className={styles.rowLink}
                            >
                              {label}
                            </Link>
                          )}
                        </span>
                        <span className={styles.rowValue}>{version.kindLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div className={styles.card}>
              <Eyebrow as="div">Sichtbar für</Eyebrow>
              <div className={styles.rows}>
                {data.visibleTo.map((line) => (
                  <div key={line.role} className={styles.row}>
                    <span className={styles.rowLabel}>{line.label}</span>
                    <span className={styles.rowValue}>
                      {[line.name, line.note].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </div>
                ))}
              </div>
              <p className={styles.cardNote}>{data.visibilityNote}</p>
            </div>
          </aside>
        </div>
      )}
      {gate.dialog}
    </div>
  );
}
